import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { Demanda, DemandaAutorizacao } from '../../../../models';
import { receivedDemandsService } from '../../../../services/receivedDemands';

export const demandasAutorizacaoQueryKey = (): QueryKey => ['demandas-autorizacao'];

interface UseDemandasAutorizacaoQueryOptions {
  staleTime?: number;
  enabled?: boolean;
}

/**
 * Hook para buscar demandas que estão aguardando autorização do setor logado
 * Usa o endpoint do backend /me/autorizacoes que retorna autorizações do setor
 */
export const useDemandasAutorizacaoQuery = (
  { staleTime = 60 * 1000, enabled = true }: UseDemandasAutorizacaoQueryOptions = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => demandasAutorizacaoQueryKey(), []);

  const mapAutorizacoesToDemandas = useCallback((autorizacoes: any[]): Demanda[] => {
    if (!Array.isArray(autorizacoes)) {
      return [];
    }

    return autorizacoes.reduce<Demanda[]>((acc, autorizacao) => {
      const rawDemanda = autorizacao?.demanda;

      if (!rawDemanda) {
        return acc;
      }

      const demandaWithMeta = {
        ...rawDemanda,
      } as Demanda & {
        demanda_autorizacao?: DemandaAutorizacao[];
        __meta?: Record<string, unknown>;
      };

      const demandaMutable = demandaWithMeta as Record<string, any>;

      const existingAutorizacoes = Array.isArray(demandaWithMeta.demanda_autorizacao)
        ? demandaWithMeta.demanda_autorizacao
        : [];

      if (!existingAutorizacoes.some((item) => item.id === autorizacao.id)) {
        demandaWithMeta.demanda_autorizacao = [...existingAutorizacoes, autorizacao];
      }

      if (autorizacao.processo) {
        demandaMutable.processo = {
          ...(demandaMutable.processo ?? {}),
          ...autorizacao.processo,
        };
      }

      if (autorizacao.processo?.setor) {
        demandaMutable.processo = {
          ...(demandaMutable.processo ?? {}),
          setor: autorizacao.processo.setor,
        };

        if (!demandaWithMeta.setor) {
          demandaMutable.setor = autorizacao.processo.setor;
        }
      }

      demandaWithMeta.__meta = {
        ...(demandaWithMeta.__meta ?? {}),
        autorizacao,
      };

      acc.push(demandaWithMeta);
      return acc;
    }, []);
  }, []);

  const query = useQuery<any[]>({
    queryKey,
    queryFn: async () => {
      try {
        // O backend retorna as autorizações do setor logado
        const autorizacoes = await receivedDemandsService.getMinhasAutorizacoes(false);

        const demandas = mapAutorizacoesToDemandas(autorizacoes);
        return demandas;
      } catch (error) {
        console.error('❌ [useDemandasAutorizacaoQuery] Erro ao buscar autorizações:', error);
        // Retornar array vazio ao invés de propagar o erro
        return [];
      }
    },
    staleTime,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    enabled: enabled, // Executa sempre que enabled=true (não depende mais de isAutorizador)
    refetchInterval: 60 * 1000, // Refetch a cada 1 minuto
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const forceRefresh = useCallback(async () => {
    try {
      const autorizacoes = await receivedDemandsService.getMinhasAutorizacoes(true);

      const demandas = mapAutorizacoesToDemandas(autorizacoes);

      queryClient.setQueryData(queryKey, demandas);
      return demandas;
    } catch (error) {
      console.error('❌ Erro ao forçar refresh de autorizações:', error);
      queryClient.setQueryData(queryKey, []);
      return [];
    }
  }, [mapAutorizacoesToDemandas, queryClient, queryKey]);

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    cacheKey: 'autorizacao',
    queryKey,
    forceRefresh,
    dataUpdatedAt: query.dataUpdatedAt,
  };
};
